import { useEffect, useRef } from 'react'

import { useAuth } from '../../../../hooks/use-auth'
import type { UsersWorkspace } from '../../../../types/user'
import { CompanyTypeEnum } from '../../../../types/workspace'
import { tokenUtils } from '../../../../utils/token-utils'
import { getFilteredCarrierOnboardingSteps } from '../_utils'

/**
 * Custom hook to handle carrier workspace setup logic
 * Finds and sets the first available carrier workspace if none is selected
 */
export const useCarrierWorkspaceSetup = () => {
  const { refetchWorkspaces, selectedWorkspace, setWorkspace, workspaces } = useAuth()
  const hasAttemptedWorkspaceSelection = useRef(false)
  const isAuthenticated = tokenUtils.isAuthenticated()

  const filteredSteps = getFilteredCarrierOnboardingSteps(selectedWorkspace?.accessType)

  useEffect(() => {
    if (!isAuthenticated || selectedWorkspace || hasAttemptedWorkspaceSelection.current) {
      return
    }

    const findAndSetCarrierWorkspace = (workspaces: UsersWorkspace[] | undefined): boolean => {
      if (!workspaces) return false

      const carrierWorkspace = workspaces.find(
        (workspace: UsersWorkspace) => workspace.workspace.workspaceType === CompanyTypeEnum.Carrier
      )

      if (carrierWorkspace) {
        setWorkspace(carrierWorkspace)
        return true
      }
      return false
    }

    const setupWorkspace = async () => {
      hasAttemptedWorkspaceSelection.current = true

      if (!findAndSetCarrierWorkspace(workspaces)) {
        try {
          const response = await refetchWorkspaces()
          findAndSetCarrierWorkspace(response.data?.myWorkspaces)
        } catch (error) {
          console.error('Error fetching workspaces:', error)
        }
      }
    }

    setupWorkspace()
  }, [isAuthenticated, selectedWorkspace, workspaces, refetchWorkspaces, setWorkspace])

  return { filteredSteps }
}
